import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Clock, LogIn, LogOut, Building2, Edit3, Check, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200',
  edited: 'bg-blue-50 text-blue-700 border border-blue-200',
  approved: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border border-red-200',
};

const VolunteerTimeLog = () => {
  const { user } = useContext(AuthContext);
  const [activeEntry, setActiveEntry] = useState(null);
  const [entries, setEntries] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  // Check-in form
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedOppId, setSelectedOppId] = useState('');
  const [showCheckInForm, setShowCheckInForm] = useState(false);

  // Edit modal
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ check_in_time: '', check_out_time: '', edit_reason: '' });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [activeRes, entriesRes, orgsRes, oppsRes] = await Promise.all([
        axios.get(`${API}/time-entries/active`),
        axios.get(`${API}/time-entries/my`),
        axios.get(`${API}/organizations/active`),
        axios.get(`${API}/opportunities`),
      ]);
      setActiveEntry(activeRes.data);
      setEntries(entriesRes.data || []);
      setOrganizations(orgsRes.data || []);
      setOpportunities((oppsRes.data || []).filter(o => o.status !== 'closed'));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!selectedOrgId) {
      toast.error('Please select an organization');
      return;
    }
    setCheckingIn(true);
    try {
      const payload = { organization_id: selectedOrgId };
      if (selectedOppId) payload.opportunity_id = selectedOppId;
      const res = await axios.post(`${API}/time-entries/check-in`, payload);
      setActiveEntry(res.data);
      setShowCheckInForm(false);
      setSelectedOrgId('');
      setSelectedOppId('');
      toast.success('Checked in!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    if (!activeEntry) return;
    setCheckingOut(true);
    try {
      await axios.post(`${API}/time-entries/${activeEntry.id}/check-out`);
      setActiveEntry(null);
      toast.success('Checked out!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    } finally {
      setCheckingOut(false);
    }
  };

  const handleRequestEdit = async () => {
    if (!editModal || !editForm.edit_reason.trim()) {
      toast.error('Please provide a reason for the edit');
      return;
    }
    try {
      const payload = { edit_reason: editForm.edit_reason };
      if (editForm.check_in_time) payload.check_in_time = new Date(editForm.check_in_time).toISOString();
      if (editForm.check_out_time) payload.check_out_time = new Date(editForm.check_out_time).toISOString();
      await axios.post(`${API}/time-entries/${editModal.id}/request-edit`, payload);
      toast.success('Edit request submitted');
      setEditModal(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Edit request failed');
    }
  };

  const openEditModal = (entry) => {
    setEditModal(entry);
    setEditForm({
      check_in_time: entry.check_in_time ? format(new Date(entry.check_in_time), "yyyy-MM-dd'T'HH:mm") : '',
      check_out_time: entry.check_out_time ? format(new Date(entry.check_out_time), "yyyy-MM-dd'T'HH:mm") : '',
      edit_reason: '',
    });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '--';
    try { return format(new Date(isoStr), 'MMM d, h:mm a'); } catch { return isoStr; }
  };

  const formatHours = (hours) => {
    if (hours == null) return '--';
    return `${hours.toFixed(1)}h`;
  };

  // Filter opportunities by selected org
  const filteredOpps = selectedOrgId
    ? opportunities.filter(o => o.partner_org_id === selectedOrgId)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF4EA]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="volunteer-time-log">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#222] mb-2">Time Log</h1>
        <p className="text-sm text-[#666] mb-6">Track your volunteer hours with check-in and check-out.</p>

        {/* ===== ACTIVE CHECK-IN BANNER ===== */}
        {activeEntry && (
          <div className="card mb-6 border-l-4 border-l-emerald-500 bg-emerald-50" data-testid="active-checkin-banner">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                  <span className="font-semibold text-emerald-800 text-lg">Checked In</span>
                </div>
                <p className="text-sm text-emerald-700">{activeEntry.organization_name}</p>
                {activeEntry.opportunity_title && (
                  <p className="text-xs text-emerald-600 mt-0.5">{activeEntry.opportunity_title}</p>
                )}
                <p className="text-xs text-emerald-600 mt-1">Since {formatTime(activeEntry.check_in_time)}</p>
              </div>
              <button
                onClick={handleCheckOut}
                disabled={checkingOut}
                className="w-full sm:w-auto bg-[#8B2E3C] hover:bg-[#6d2330] text-white font-bold py-4 px-8 rounded-xl text-lg transition-all active:scale-95 disabled:opacity-50"
                data-testid="check-out-button"
              >
                <div className="flex items-center justify-center gap-2">
                  <LogOut className="w-5 h-5" />
                  {checkingOut ? 'Checking Out...' : 'Check Out'}
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ===== CHECK-IN SECTION ===== */}
        {!activeEntry && (
          <div className="mb-6">
            {!showCheckInForm ? (
              <button
                onClick={() => setShowCheckInForm(true)}
                className="w-full bg-[#8B2E3C] hover:bg-[#6d2330] text-white font-bold py-5 px-6 rounded-xl text-xl transition-all active:scale-[0.98]"
                data-testid="start-check-in-button"
              >
                <div className="flex items-center justify-center gap-3">
                  <LogIn className="w-6 h-6" />
                  Check In
                </div>
              </button>
            ) : (
              <div className="card" data-testid="check-in-form">
                <h2 className="text-lg font-bold text-[#222] mb-4">Check In</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#444] mb-1">Organization *</label>
                    <select
                      value={selectedOrgId}
                      onChange={(e) => { setSelectedOrgId(e.target.value); setSelectedOppId(''); }}
                      className="input-field text-base py-3"
                      data-testid="org-select"
                    >
                      <option value="">Select organization...</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.org_name}</option>
                      ))}
                    </select>
                  </div>

                  {selectedOrgId && filteredOpps.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-[#444] mb-1">Opportunity (optional)</label>
                      <select
                        value={selectedOppId}
                        onChange={(e) => setSelectedOppId(e.target.value)}
                        className="input-field text-base py-3"
                        data-testid="opp-select"
                      >
                        <option value="">General volunteering (no specific event)</option>
                        {filteredOpps.map(opp => (
                          <option key={opp.id} value={opp.id}>{opp.title} — {opp.date}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleCheckIn}
                      disabled={checkingIn || !selectedOrgId}
                      className="flex-1 bg-[#8B2E3C] hover:bg-[#6d2330] text-white font-bold py-4 rounded-xl text-lg transition-all active:scale-95 disabled:opacity-50"
                      data-testid="confirm-check-in-button"
                    >
                      {checkingIn ? 'Checking In...' : 'Check In Now'}
                    </button>
                    <button
                      onClick={() => { setShowCheckInForm(false); setSelectedOrgId(''); setSelectedOppId(''); }}
                      className="px-6 py-4 rounded-xl border-2 border-[#E9DDCF] text-[#666] font-medium hover:bg-white transition-colors"
                      data-testid="cancel-check-in-button"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== TOTAL HOURS ===== */}
        <div className="card mb-6 flex items-center gap-4" data-testid="total-hours-card">
          <div className="bg-[#8B2E3C] p-3 rounded-lg">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold text-[#222]">{(user?.total_approved_hours || 0).toFixed(1)}h</div>
            <div className="text-sm text-[#666]">Total Approved Hours</div>
          </div>
        </div>

        {/* ===== TIME ENTRIES LIST ===== */}
        <h2 className="text-xl font-bold text-[#222] mb-3">My Time Entries</h2>

        {entries.length === 0 ? (
          <div className="card text-center py-10">
            <Clock className="w-10 h-10 text-[#ccc] mx-auto mb-3" />
            <p className="text-[#888]">No time entries yet. Check in to start logging hours!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map(entry => (
              <div key={entry.id} className="card" data-testid={`time-entry-${entry.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Building2 className="w-4 h-4 text-[#8B2E3C] shrink-0" />
                      <span className="font-semibold text-[#222] truncate">{entry.organization_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[entry.status]}`}>
                        {entry.status}
                      </span>
                    </div>
                    {entry.opportunity_title && (
                      <p className="text-xs text-[#888] ml-6 mb-1">{entry.opportunity_title}</p>
                    )}
                    <div className="text-sm text-[#666] ml-6 space-y-0.5">
                      <div>In: {formatTime(entry.check_in_time)}</div>
                      <div>Out: {formatTime(entry.check_out_time)}</div>
                    </div>
                    {entry.edited_flag && entry.edit_reason && (
                      <div className="text-xs text-blue-600 ml-6 mt-1 flex items-center gap-1">
                        <Edit3 className="w-3 h-3" /> {entry.edit_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                    <div className="text-xl font-bold text-[#8B2E3C]">{formatHours(entry.total_hours)}</div>
                    {entry.status !== 'approved' && entry.check_out_time && (
                      <button
                        onClick={() => openEditModal(entry)}
                        className="text-xs text-[#8B2E3C] hover:underline flex items-center gap-1"
                        data-testid={`edit-entry-${entry.id}`}
                      >
                        <Edit3 className="w-3 h-3" /> Request Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* ===== EDIT MODAL ===== */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="edit-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-[#222] mb-4">Request Time Edit</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Check-In Time</label>
                <input
                  type="datetime-local"
                  value={editForm.check_in_time}
                  onChange={(e) => setEditForm(f => ({ ...f, check_in_time: e.target.value }))}
                  className="input-field"
                  data-testid="edit-check-in-time"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Check-Out Time</label>
                <input
                  type="datetime-local"
                  value={editForm.check_out_time}
                  onChange={(e) => setEditForm(f => ({ ...f, check_out_time: e.target.value }))}
                  className="input-field"
                  data-testid="edit-check-out-time"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Reason for edit *</label>
                <textarea
                  value={editForm.edit_reason}
                  onChange={(e) => setEditForm(f => ({ ...f, edit_reason: e.target.value }))}
                  className="input-field min-h-[80px]"
                  placeholder="e.g., Missed check-in, incorrect time..."
                  data-testid="edit-reason"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleRequestEdit}
                  className="flex-1 btn-primary py-3"
                  data-testid="submit-edit-button"
                >
                  Submit Request
                </button>
                <button
                  onClick={() => setEditModal(null)}
                  className="px-6 py-3 rounded-lg border-2 border-[#E9DDCF] text-[#666] font-medium hover:bg-[#FBF4EA] transition-colors"
                  data-testid="cancel-edit-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerTimeLog;
